/**
 * QuickCalc — Lovable-aligned simplification (cockpit-lovable-alignment /fix
 * 2026-05-15). Port of `dashboard-arcanum/src/pages/Cockpit.tsx:254-340` with:
 *
 *   - 5 tabs retained (Cap Rate→Value, DSCR, Cash-on-Cash, Max Offer, Rent/SF).
 *     Max Offer kept per user override of the initial Lovable 4-tab shape.
 *   - Direct-formula calculations (no derived debt-service-from-loan/rate/amort).
 *     Each tab now takes the inputs it actually needs and computes the output
 *     in one step.
 *   - DSCR + Cash-on-Cash retain threshold-based color coding via
 *     `dscrColor` / `cocColor` (≥1.25 / ≥0.08 accent · ≥1.0 / ≥0.04 warn ·
 *     else danger). Other tabs render in uniform `text-text-bright`.
 *   - Lovable layout pattern: content flows top-down (inputs → result → CTA
 *     → empty space), NOT centered/stretched. Panel container stays full row-1
 *     height; the QuickCalc body just flows naturally inside it.
 *   - Lovable input-row shape: 92px fixed-width label column + flex-1 input
 *     box + optional suffix outside. No `$` prefix INSIDE the input — bare
 *     numbers per Lovable.
 *
 * Layout invariants:
 *   - Tab body is a Fragment under <Panel> body (which is `flex flex-col
 *     overflow-hidden min-h-0`); children flow top-down with default justify.
 *   - CTA carries `shrink-0` so it can never compress.
 *   - No `overflow-y-auto` — content MUST fit; if a future tab grows past
 *     the panel allocation, the layout breaks visibly and the fix is to
 *     tighten input/result padding here, NOT to add scrollbars.
 */

import { useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from '@tanstack/react-router';
import { Panel } from '@/components/ui';
import { formatCurrency, formatPercent, formatMultiple } from '@/lib/format';

const TABS = ['Cap Rate → Value', 'DSCR', 'Cash-on-Cash', 'Max Offer', 'Rent/SF'] as const;

function dscrColor(v: number): string {
  if (v >= 1.25) return 'text-accent';
  if (v >= 1.0) return 'text-warn';
  return 'text-danger';
}

function cocColor(v: number): string {
  if (v >= 0.08) return 'text-accent';
  if (v >= 0.04) return 'text-warn';
  return 'text-danger';
}

function parseNum(s: string): number {
  return parseFloat(s.replace(/,/g, '')) || 0;
}

// ── Shared sub-components (Lovable input-row + result block) ─────

/**
 * Lovable input row — `Cockpit.tsx:272-281` verbatim shape.
 * 92px label column · flex-1 input box · optional suffix outside.
 * NO prefix inside input; values display as bare numbers.
 */
function CalcInput({ label, value, onChange, suffix }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  suffix?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-text-muted font-mono w-[92px] shrink-0">{label}</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        className="flex-1 min-w-0 py-1.5 px-2.5 bg-input-bg border border-input-border rounded-[2px] text-text-bright text-[15px] font-mono outline-none focus:border-accent/30"
      />
      {suffix && <span className="text-text-dim font-mono text-[11px] shrink-0">{suffix}</span>}
    </div>
  );
}

/**
 * Lovable result block — `Cockpit.tsx:319-329`. Divider + small uppercase
 * label + large value + optional sub-line. `mt-[22px] pt-[14px]` matches
 * Lovable exactly.
 */
function CalcResult({ label, value, sub, colorClass }: {
  label: string;
  value: string;
  sub?: React.ReactNode;
  colorClass?: string;
}) {
  return (
    <div className="text-center mt-[12px] pt-[10px] border-t border-panel-border">
      <div className="text-[8px] text-text-dim font-mono tracking-[2px] mb-0.5">{label}</div>
      <div className={clsx('text-2xl font-bold font-mono tracking-[-1px] tabular-nums', colorClass || 'text-text-bright')}>
        {value}
      </div>
      {sub && (
        <div className="text-[9px] text-text-dim font-mono mt-0.5 tabular-nums">{sub}</div>
      )}
    </div>
  );
}

// Mirrors Button primary variant verbatim per Button.tsx:11. The
// hover:shadow-[0_0_10px_#00c85333] @design-token resolves --color-accent
// at alpha 33; the in-class hex literal can't be replaced with a token name
// because Tailwind's box-shadow utility doesn't propagate from `bg-*` classes.
const CTA_BUTTON_CLASS = 'w-full py-2 mt-[10px] shrink-0 bg-accent-dim border border-accent/20 rounded-[2px] text-accent text-[10px] font-mono font-semibold cursor-pointer tracking-[0.5px] animate-cta-idle hover:scale-[1.015] hover:shadow-[0_0_10px_#00c85333] hover:border-accent/27 hover:bg-accent/13 transition-all duration-300'; // @design-token

function CtaButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} className={CTA_BUTTON_CLASS}>
      &rarr; Build Model in Workbench
    </button>
  );
}

// ── Tab content components (direct-formula simplification) ───────

function CapRateTab({ onCta }: { onCta: () => void }) {
  const [noi, setNoi] = useState('500000');
  const [capRate, setCapRate] = useState('5.5');
  const [units, setUnits] = useState('130');

  const noiN = parseNum(noi);
  const capN = parseNum(capRate);
  const unitsN = parseNum(units);
  const val = capN > 0 ? noiN / (capN / 100) : 0;
  const perUnit = unitsN > 0 ? val / unitsN : 0;

  return (
    <>
      <div className="flex flex-col gap-1.5 shrink-0">
        <CalcInput label="NOI" value={noi} onChange={setNoi} />
        <CalcInput label="Cap Rate" value={capRate} onChange={setCapRate} suffix="%" />
        <CalcInput label="Units" value={units} onChange={setUnits} />
      </div>
      <CalcResult
        label="INDICATED VALUE"
        value={formatCurrency(val)}
        sub={<>Per Unit: {formatCurrency(perUnit, 2)} &middot; {unitsN.toLocaleString('en-US')} units</>}
      />
      <CtaButton onClick={onCta} />
    </>
  );
}

function DscrTab({ onCta }: { onCta: () => void }) {
  const [noi, setNoi] = useState('500000');
  const [debtService, setDebtService] = useState('400000');

  const noiN = parseNum(noi);
  const dsN = parseNum(debtService);
  const dscr = dsN > 0 ? noiN / dsN : 0;

  return (
    <>
      <div className="flex flex-col gap-1.5 shrink-0">
        <CalcInput label="NOI" value={noi} onChange={setNoi} />
        <CalcInput label="Debt Service" value={debtService} onChange={setDebtService} />
      </div>
      <CalcResult
        label="DEBT SERVICE COVERAGE RATIO"
        value={formatMultiple(dscr)}
        colorClass={dscrColor(dscr)}
      />
      <CtaButton onClick={onCta} />
    </>
  );
}

function CocTab({ onCta }: { onCta: () => void }) {
  const [cashFlow, setCashFlow] = useState('85000');
  const [equity, setEquity] = useState('1000000');

  const cfN = parseNum(cashFlow);
  const eqN = parseNum(equity);
  const coc = eqN > 0 ? cfN / eqN : 0;

  return (
    <>
      <div className="flex flex-col gap-1.5 shrink-0">
        <CalcInput label="Cash Flow" value={cashFlow} onChange={setCashFlow} />
        <CalcInput label="Equity" value={equity} onChange={setEquity} />
      </div>
      <CalcResult
        label="CASH-ON-CASH RETURN"
        value={formatPercent(coc)}
        colorClass={cocColor(coc)}
      />
      <CtaButton onClick={onCta} />
    </>
  );
}

function MaxOfferTab({ onCta }: { onCta: () => void }) {
  const [noi, setNoi] = useState('500000');
  const [targetCap, setTargetCap] = useState('5.5');
  const [maxLtc, setMaxLtc] = useState('65');

  const noiN = parseNum(noi);
  const capN = parseNum(targetCap);
  const ltcN = parseNum(maxLtc);

  const maxPrice = capN > 0 ? noiN / (capN / 100) : 0;
  const maxLoan = maxPrice * (ltcN / 100);
  const requiredEquity = maxPrice - maxLoan;

  return (
    <>
      <div className="flex flex-col gap-1.5 shrink-0">
        <CalcInput label="NOI" value={noi} onChange={setNoi} />
        <CalcInput label="Target Cap" value={targetCap} onChange={setTargetCap} suffix="%" />
        <CalcInput label="Max LTC" value={maxLtc} onChange={setMaxLtc} suffix="%" />
      </div>
      <CalcResult
        label="MAX PURCHASE PRICE"
        value={formatCurrency(maxPrice)}
        sub={<>Max Loan: {formatCurrency(maxLoan)} &middot; Equity: {formatCurrency(requiredEquity)}</>}
      />
      <CtaButton onClick={onCta} />
    </>
  );
}

function RentSfTab({ onCta }: { onCta: () => void }) {
  const [avgRent, setAvgRent] = useState('2100');
  const [avgSf, setAvgSf] = useState('910');

  const rentN = parseNum(avgRent);
  const sfN = parseNum(avgSf);
  const rentPerSf = sfN > 0 ? rentN / sfN : 0;

  return (
    <>
      <div className="flex flex-col gap-1.5 shrink-0">
        <CalcInput label="Average Rent" value={avgRent} onChange={setAvgRent} />
        <CalcInput label="Avg Unit SF" value={avgSf} onChange={setAvgSf} />
      </div>
      <CalcResult
        label="RENT PER SF"
        value={`$${rentPerSf.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`}
      />
      <CtaButton onClick={onCta} />
    </>
  );
}

// ── Main component ───────────────────────────────────────────────

export function QuickCalc() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const goBuilder = () =>
    navigate({ to: '/builder', search: (prev) => ({ ...prev, tab: 'workbench' as const }) });

  return (
    <Panel title="Quick Calc" right={<span className="text-[8px] text-text-dim font-mono">{today}</span>} className="h-full">
      {/* Tab strip — Lovable `Cockpit.tsx:261-270` verbatim metrics. */}
      <div className="flex gap-px shrink-0 bg-input-bg border border-input-border p-0.5 rounded-[2px] mb-[10px]">
        {TABS.map((t, i) => (
          <button
            key={i}
            onClick={() => setTab(i)}
            className={clsx(
              'flex-1 py-[5px] px-[3px] text-[9.5px] font-mono rounded-[2px] border-none cursor-pointer transition-colors whitespace-nowrap',
              tab === i
                ? 'text-text-bright bg-panel-border-hi font-semibold'
                : 'text-text-dim bg-transparent hover:text-text-muted'
            )}
          >{t}</button>
        ))}
      </div>

      {/* Tab body: content flows top-down. NO `flex-1`/`justify-center`
       * wrapper — empty space accumulates at the panel bottom by design
       * (matches Lovable behavior across all 5 tabs). */}
      {tab === 0 && <CapRateTab onCta={goBuilder} />}
      {tab === 1 && <DscrTab onCta={goBuilder} />}
      {tab === 2 && <CocTab onCta={goBuilder} />}
      {tab === 3 && <MaxOfferTab onCta={goBuilder} />}
      {tab === 4 && <RentSfTab onCta={goBuilder} />}
    </Panel>
  );
}
